import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import JobCard from '../components/JobCard';
import LanguageSelector from '../components/LanguageSelector';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareersPage = () => {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchQuery, positionFilter, locationFilter]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Position type filter
    if (positionFilter && positionFilter !== 'all') {
      filtered = filtered.filter(job => job.position_type === positionFilter);
    }

    // Location filter
    if (locationFilter && locationFilter !== 'all') {
      filtered = filtered.filter(job => job.location === locationFilter);
    }

    setFilteredJobs(filtered);
  };

  // Get unique position types and locations
  const positionTypes = ['all', ...new Set(jobs.map(job => job.position_type))];
  const locations = ['all', ...new Set(jobs.map(job => job.location))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900" data-testid="site-title">
              {t('siteTitle')}
            </h1>
          </div>
          <LanguageSelector />
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758448511255-ac2a24a135d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwYnJpZ2h0JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzY0NDU1OTM3fDA&ixlib=rb-4.1.0&q=85"
          alt="Hero"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/30 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="careers-title">
              {t('careersTitle')}
            </h2>
            <p className="text-xl text-white/90" data-testid="careers-subtitle">
              {t('careersSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative" data-testid="search-container">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                data-testid="search-input"
              />
            </div>

            {/* Position Type Filter */}
            <Select value={positionFilter} onValueChange={setPositionFilter}>
              <SelectTrigger className="bg-white" data-testid="position-filter">
                <SelectValue placeholder={t('filterPosition')} />
              </SelectTrigger>
              <SelectContent>
                {positionTypes.map(type => (
                  <SelectItem key={type} value={type} data-testid={`position-option-${type}`}>
                    {type === 'all' ? t('allPositions') : type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Location Filter */}
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="bg-white" data-testid="location-filter">
                <SelectValue placeholder={t('filterLocation')} />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc} data-testid={`location-option-${loc}`}>
                    {loc === 'all' ? t('allLocations') : loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12" data-testid="no-jobs-message">
            <p className="text-slate-600 text-lg">{t('noJobs')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-grid">
            {filteredJobs.map(job => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CareersPage;